import { date, number, object, string, type InferType } from "yup";

export type PayableDocument = {
  name: string;
  size: number;
  s3ObjectKey: string;
  localUrl: string;
  file: File;
  contentType?: string;
};

export type PayableWorkflowProps = {
  onExit: () => void;
  onComplete?: (paymentBatchId: string) => void;
};

export enum PayableFormFields {
  ArenaId = "arenaId",
  PayeeId = "payeeId",
  PayeeType = "payeeType",
  InvoiceNumber = "invoiceNumber",
  Amount = "amount",
  InvoiceDate = "invoiceDate",
  DueDate = "dueDate",
  Description = "description",
  EntityId = "entityId",
}

export const payableInitialValues: PayableFormValues = {
  payeeType: "",
  [PayableFormFields.ArenaId]: "",
  [PayableFormFields.PayeeId]: "",
  [PayableFormFields.EntityId]: "",
  [PayableFormFields.InvoiceNumber]: "",
  [PayableFormFields.Amount]: null,
  [PayableFormFields.InvoiceDate]: null,
  [PayableFormFields.DueDate]: null,
  [PayableFormFields.Description]: "",
};

export const payableDetailsValidationSchema = object().shape({
  [PayableFormFields.ArenaId]: string()
    .required("Client is required")
    .min(1, "Client is required"),
  [PayableFormFields.PayeeId]: string()
    .required("Payee is required")
    .min(1, "Payee is required"),
  payeeType: string().required(),
  [PayableFormFields.EntityId]: string()
    .required("Investment entity is required")
    .min(1, "Investment entity is required"),
  [PayableFormFields.InvoiceNumber]: string().when("payeeType", {
    is: (value: string) => value?.toLowerCase() === "vendor",
    then: (schema) => schema.required("Invoice number is required"),
    otherwise: (schema) => schema.optional(),
  }),
  [PayableFormFields.Amount]: number()
    .transform((value: unknown, originalValue: unknown): number => {
      if (typeof originalValue === "string") {
        const parsed = parseFloat(originalValue);
        return isNaN(parsed) ? 0 : parsed;
      }
      return typeof value === "number" ? value : 0;
    })
    .required("Amount is required")
    .min(0.01, "Amount must be greater than 0")
    .test("max-decimal-places", "Amount cannot have more than 2 decimal places", (value) => {
      if (value == null) return true;
      const decimalPlaces = (value.toString().split(".")[1] || "").length;
      return decimalPlaces <= 2;
    }),
  [PayableFormFields.InvoiceDate]: date()
    .nullable()
    .when("payeeType", {
      is: (value: string) => value?.toLowerCase() === "vendor",
      then: (schema) => schema.required("Invoice date is required"),
      otherwise: (schema) => schema.optional(),
    }),
  [PayableFormFields.DueDate]: date()
    .nullable()
    .when("payeeType", {
      is: (value: string) => value?.toLowerCase() === "vendor",
      then: (schema) =>
        schema
          .required("Due date is required")
          .when([PayableFormFields.InvoiceDate], ([invoiceDate], schema) => {
            if (invoiceDate) {
              return schema.min(invoiceDate, "Due date must be on or after the invoice date");
            }
            return schema;
          }),
      otherwise: (schema) => schema.optional().nullable(),
    }),
  [PayableFormFields.Description]: string().optional(),
});

export interface PayableFormValues {
  [PayableFormFields.ArenaId]: string;
  [PayableFormFields.PayeeId]: string;
  payeeType: string;
  [PayableFormFields.EntityId]: string;
  [PayableFormFields.InvoiceNumber]?: string | undefined;
  [PayableFormFields.Amount]: number | null;
  [PayableFormFields.InvoiceDate]?: Date | null;
  [PayableFormFields.DueDate]?: Date | null;
  [PayableFormFields.Description]?: string | undefined;
}

export enum PayableWorkflowStage {
  Upload = "upload",
  Form = "form",
  Payment = "payment",
}

/**
 * Step 1: Client Step validation schema
 * Validates client, payee selection, and invoice details (vendor only)
 * Note: Invoice amount is NOT collected in Step 1 - only payment amount in Step 2
 */
export const clientStepValidationSchema = object().shape({
  [PayableFormFields.ArenaId]: string()
    .required("Client is required")
    .min(1, "Client is required"),
  [PayableFormFields.PayeeId]: string()
    .required("Payee is required")
    .min(1, "Payee is required"),
  payeeType: string().required(),
  // Invoice details fields (vendor only) - no invoiceAmount here
  [PayableFormFields.InvoiceNumber]: string().when("payeeType", {
    is: (value: string) => value?.toLowerCase() === "vendor",
    then: (schema) => schema.required("Invoice number is required"),
    otherwise: (schema) => schema.optional(),
  }),
  [PayableFormFields.InvoiceDate]: date()
    .nullable()
    .when("payeeType", {
      is: (value: string) => value?.toLowerCase() === "vendor",
      then: (schema) => schema.required("Invoice date is required"),
      otherwise: (schema) => schema.optional(),
    }),
  [PayableFormFields.DueDate]: date()
    .nullable()
    .when("payeeType", {
      is: (value: string) => value?.toLowerCase() === "vendor",
      then: (schema) =>
        schema
          .required("Due date is required")
          .when([PayableFormFields.InvoiceDate], ([invoiceDate], schema) => {
            if (invoiceDate) {
              return schema.min(invoiceDate, "Due date must be on or after the invoice date");
            }
            return schema;
          }),
      otherwise: (schema) => schema.optional().nullable(),
    }),
  [PayableFormFields.Description]: string().optional(),
});

export type ClientStepFormValues = InferType<typeof clientStepValidationSchema>;

/**
 * Step 2: Payment Details Step validation schema
 * Validates entity and payment details only (invoice details removed)
 */
export const paymentDetailsStepValidationSchema = object().shape({
  [PayableFormFields.EntityId]: string()
    .required("Investment entity is required")
    .min(1, "Investment entity is required"),
  paymentAmount: number()
    .nullable()
    .transform((value: unknown, originalValue: unknown): number | null => {
      // Transform empty/invalid values to null for proper placeholder display
      if (originalValue === "" || originalValue === undefined || originalValue === null) {
        return null;
      }
      if (typeof originalValue === "string") {
        const parsed = parseFloat(originalValue);
        return isNaN(parsed) ? null : parsed;
      }
      return typeof value === "number" ? value : null;
    })
    .typeError("Payment amount must be a number")
    .required("Payment amount is required")
    .min(0.01, "Payment amount must be greater than 0")
    .test("max-decimal-places", "Amount cannot have more than 2 decimal places", (value) => {
      if (value == null) return true;
      const decimalPlaces = (value.toString().split(".")[1] || "").length;
      return decimalPlaces <= 2;
    }),
  paymentDate: date()
    .typeError("Payment date is required")
    .required("Payment date is required"),
  paymentDescription: string().nullable().max(250, "Must be at most 250 characters"),
});

export type PaymentDetailsStepFormValues = InferType<typeof paymentDetailsStepValidationSchema>;

/**
 * Initial values type for PaymentDetailsStep form.
 * Allows undefined for paymentAmount so placeholder can display when field is empty.
 */
export type PaymentDetailsStepInitialValues = Omit<
  PaymentDetailsStepFormValues,
  "paymentAmount"
> & {
  paymentAmount?: number | undefined;
};

/**
 * Workflow step type for StepIndicator
 */
export type PayableWorkflowStep = {
  index: number;
  name: string;
  url: string;
  status: "active" | "complete" | "inactive";
};
